import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, effect, inject, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { LanguageService } from '../services/language.service';
import { CurrencyResolverService } from '../services/currency-resolver.service';
import {
  MarketplaceFilterOption,
  MarketplaceSignals,
  PublicAvailability,
  PublicAvailabilityFilterOptions,
  PublicAvailabilitySearchParams,
  PublicPage,
  PublicTrustSummary
} from './public-marketplace.models';
import { PublicMarketplaceService } from './public-marketplace.service';

@Component({
  selector: 'app-public-availabilities-list',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    SelectModule
  ],
  templateUrl: './public-availabilities-list.component.html',
  styleUrls: ['./public-marketplace.shared.css']
})
export class PublicAvailabilitiesListComponent implements OnInit {
  private static readonly FILTER_STORAGE_KEY = 'loadgate:public-availabilities:filters:v1';
  private readonly fb = inject(FormBuilder);
  private readonly currencyResolver = inject(CurrencyResolverService);
  private readonly service = inject(PublicMarketplaceService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly languageService = inject(LanguageService);

  readonly pageSize = 12;
  filtersOpen = false;

  form = this.fb.group({
    corridorOrigin: [''],
    corridorDestination: [''],
    equipmentType: [''],
    weightKg: [''],
    pallets: ['']
  });

  filterOptions: PublicAvailabilityFilterOptions = {
    corridorOrigins: [],
    corridorDestinations: [],
    equipmentTypes: [],
    currencies: [],
    corridorOriginOptions: [],
    corridorDestinationOptions: [],
    equipmentTypeOptions: []
  };
  page: PublicPage<PublicAvailability> | null = null;
  availabilities: PublicAvailability[] = [];
  loading = false;
  error = '';
  private initialized = false;
  private suppressFilterPersistence = false;
  private restoredPageNumber = 0;
  private pendingScrollRestore: number | null = null;

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();
      if (!this.initialized) {
        return;
      }
      this.loadFilterOptions();
      this.loadAvailabilities(this.page?.number ?? 0);
    });
  }

  get originOptions(): Array<{ label: string; value: string }> {
    return this.preferredOptions(this.filterOptions.corridorOriginOptions, this.filterOptions.corridorOrigins);
  }

  get destinationOptions(): Array<{ label: string; value: string }> {
    return this.preferredOptions(this.filterOptions.corridorDestinationOptions, this.filterOptions.corridorDestinations);
  }

  get equipmentOptions(): Array<{ label: string; value: string }> {
    return this.preferredOptions(this.filterOptions.equipmentTypeOptions, this.filterOptions.equipmentTypes);
  }

  ngOnInit(): void {
    this.restoreFilterState();
    this.initialized = true;
    this.loadFilterOptions();
    this.loadAvailabilities(this.restoredPageNumber);
  }

  applyFilters(): void {
    this.pendingScrollRestore = 0;
    this.loadAvailabilities(0);
  }

  toggleFilters(): void {
    this.filtersOpen = !this.filtersOpen;
  }

  clearFilters(): void {
    this.clearStoredFilterState();
    this.suppressFilterPersistence = true;
    this.form.reset({
      corridorOrigin: '',
      corridorDestination: '',
      equipmentType: '',
      weightKg: '',
      pallets: ''
    });
    this.pendingScrollRestore = 0;
    this.loadAvailabilities(0);
    this.suppressFilterPersistence = false;
  }

  previousPage(): void {
    if (!this.page || this.page.number === 0) {
      return;
    }
    this.pendingScrollRestore = 0;
    this.loadAvailabilities(this.page.number - 1);
  }

  nextPage(): void {
    if (!this.page || this.page.number + 1 >= this.page.totalPages) {
      return;
    }
    this.pendingScrollRestore = 0;
    this.loadAvailabilities(this.page.number + 1);
  }

  login(): void {
    this.router.navigate(['/login'], {
      queryParams: { returnUrl: this.router.url || '/' }
    });
  }

  loadAvailabilities(page: number): void {
    this.restoredPageNumber = page;
    this.persistFilterState();
    this.loading = true;
    this.error = '';
    this.service.getAvailabilities(this.buildParams(page)).subscribe({
      next: result => {
        this.page = result;
        this.availabilities = result.content ?? [];
        this.loading = false;
        this.restoreScrollPositionOnce();
        this.cdr.detectChanges();
      },
      error: () => {
        this.error = this.text('Unable to load public availability right now.', 'تعذر تحميل التوفرات العامة حالياً.');
        this.availabilities = [];
        this.page = null;
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  trackById(_: number, availability: PublicAvailability): number {
    return availability.id;
  }

  openAvailability(availabilityId: number): void {
    this.persistFilterState();
    this.router.navigate(['/public/availabilities', availabilityId]);
  }

  formatDate(value?: string | null): string {
    if (!value) {
      return this.text('Flexible', 'مرن');
    }
    return new Intl.DateTimeFormat(this.locale(), {
      timeZone: 'Asia/Riyadh',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(this.parseDate(value) ?? new Date(value));
  }

  formatRelativeTime(value?: string | null): string {
    const date = this.parseDate(value);
    if (!date) {
      return this.text('Recently', 'حديثًا');
    }

    const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) {
      return this.text('just now', 'الآن');
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return this.languageService.currentLanguage() === 'ar'
        ? minutes === 1
          ? 'منذ دقيقة'
          : `منذ ${minutes} دقائق`
        : `${minutes} ${minutes === 1 ? 'min' : 'mins'} ago`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return this.languageService.currentLanguage() === 'ar'
        ? hours === 1
          ? 'منذ ساعة'
          : `منذ ${hours} ساعات`
        : `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
    }

    const days = Math.floor(hours / 24);
    if (days < 30) {
      return this.languageService.currentLanguage() === 'ar'
        ? days === 1
          ? 'منذ يوم'
          : `منذ ${days} أيام`
        : `${days} ${days === 1 ? 'day' : 'days'} ago`;
    }

    const months = Math.floor(days / 30);
    if (months < 12) {
      return this.languageService.currentLanguage() === 'ar'
        ? months === 1
          ? 'منذ شهر'
          : `منذ ${months} أشهر`
        : `${months} ${months === 1 ? 'month' : 'months'} ago`;
    }

    const years = Math.floor(months / 12);
    return this.languageService.currentLanguage() === 'ar'
      ? years === 1
        ? 'منذ سنة'
        : `منذ ${years} سنوات`
      : `${years} ${years === 1 ? 'year' : 'years'} ago`;
  }

  formatMoney(value?: number | null, currency?: string | null): string {
    if (value === null || value === undefined) {
      return this.text('Price on request', 'السعر عند الطلب');
    }
    return this.currencyResolver.formatCurrency(value, {
      currencyCode: currency,
      locale: this.locale(),
      maximumFractionDigits: 0
    });
  }

  signalBadges(availability: PublicAvailability): Array<{ icon: string; label: string; strong?: boolean }> {
    const badges: Array<{ icon: string; label: string; strong?: boolean }> = [];
    this.addDemandBadges(badges, availability.signals);
    return badges;
  }

  joinList(values?: string[] | null): string {
    return values?.filter(Boolean).join(', ') || this.text('Any', 'أي');
  }

  trustTitle(trust?: PublicTrustSummary | null): string {
    if (!this.isTrustAvailable(trust)) {
      return this.text('Trust profile not available', 'ملف الثقة غير متاح');
    }
    return trust?.trustBadge || this.translateTrustValue(trust?.verificationStatus) || this.text('Trust reviewed', 'تمت مراجعة ملف الثقة');
  }

  trustSubtitle(trust?: PublicTrustSummary | null): string {
    const availableTrust = this.availableTrust(trust);
    if (!availableTrust) {
      return this.text('Trust profile not available.', 'ملف الثقة غير متاح.');
    }
    return (
      availableTrust.publicRecommendationNote ||
      this.trustSignalText(availableTrust) ||
      this.text('Redacted trust signals available.', 'إشارات الثقة المنقحة متاحة.')
    );
  }

  labelize(value?: string | null): string {
    return value ? value.replace(/_/g, ' ').toLowerCase() : '';
  }

  isTrustAvailable(trust?: PublicTrustSummary | null): boolean {
    return this.availableTrust(trust) !== null;
  }

  private availableTrust(trust?: PublicTrustSummary | null): PublicTrustSummary | null {
    if (!trust) {
      return null;
    }
    const status = (trust.verificationStatus || '').trim().toLowerCase();
    if (status === 'unverified') {
      return null;
    }
    return trust;
  }

  trustScoreLabel(trust?: PublicTrustSummary | null): string {
    if (!this.isTrustAvailable(trust) || trust?.trustScore === null || trust?.trustScore === undefined) {
      return '';
    }
    return `${trust.trustScore}/100`;
  }

  text(en: string, ar: string): string {
    return this.languageService.currentLanguage() === 'ar' ? ar : en;
  }

  private translateTrustValue(value?: string | null): string {
    const key = (value || '').trim().toUpperCase();
    const map: Record<string, string> = {
      VERIFIED: this.text('Verified', 'موثّق'),
      UNVERIFIED: this.text('Unverified', 'غير موثّق'),
      LIMITED_HISTORY: this.text('Limited history', 'سجل محدود'),
      COMMUNICATION_ONLY: this.text('Communication only', 'التواصل فقط'),
      MARKETPLACE_LEAD: this.text('Marketplace lead', 'ملف جديد'),
      TRUSTED: this.text('Trusted', 'موثوق'),
      EMERGING: this.text('Emerging', 'في البداية'),
      ESTABLISHING: this.text('Establishing', 'في طور البناء')
    };
    return map[key] || this.labelize(value) || '';
  }

  private addDemandBadges(
    badges: Array<{ icon: string; label: string; strong?: boolean }>,
    signals?: MarketplaceSignals | null
  ): void {
    if (!signals) return;
    if ((signals.viewCount || 0) > 0) {
      badges.push({
        icon: 'pi pi-eye',
        label: this.languageService.currentLanguage() === 'ar'
          ? `تمت المشاهدة ${signals.viewCount} مرة`
          : `Viewed ${signals.viewCount} times`
      });
    }
    if ((signals.offersSubmittedCount || 0) > 0) {
      badges.push({
        icon: 'pi pi-send',
        label: this.languageService.currentLanguage() === 'ar'
          ? `${signals.offersSubmittedCount} عروض`
          : `${signals.offersSubmittedCount} offers`
      });
    }
    if ((signals.interestedUsersCount || 0) > 0) {
      badges.push({
        icon: 'pi pi-users',
        label: this.languageService.currentLanguage() === 'ar'
          ? `${signals.interestedUsersCount} مهتم`
          : `${signals.interestedUsersCount} interested`
      });
    }
    if (signals.hotLane) badges.push({ icon: 'pi pi-bolt', label: this.text('Hot lane', 'مسار ساخن'), strong: true });
    if (signals.fastPickup) badges.push({ icon: 'pi pi-stopwatch', label: this.text('Fast pickup', 'استلام سريع'), strong: true });
  }

  onLocationOptionFilter(type: 'origin' | 'destination' | 'equipment', event: unknown): void {
    const term = this.extractFilterTerm(event);
    if (!term) {
      this.setLazyOptions(type, []);
      return;
    }

    const params =
      type === 'origin'
        ? { corridorOrigin: term }
        : type === 'destination'
          ? { corridorDestination: term }
          : { equipmentType: term };

    this.service.getAvailabilityFilterOptions({ ...params, lang: this.languageService.currentLanguage() }).subscribe({
      next: options => {
        const values =
          type === 'origin'
            ? options.corridorOrigins
            : type === 'destination'
              ? options.corridorDestinations
              : options.equipmentTypes;
        const displayOptions =
          type === 'origin'
            ? options.corridorOriginOptions
            : type === 'destination'
              ? options.corridorDestinationOptions
              : options.equipmentTypeOptions;
        this.setLazyOptions(type, values ?? [], displayOptions ?? this.toOptions(values));
        this.cdr.detectChanges();
      },
      error: () => {
        this.setLazyOptions(type, []);
        this.cdr.detectChanges();
      }
    });
  }

  private buildParams(page: number): PublicAvailabilitySearchParams {
    const value = this.form.getRawValue();
    return {
      page,
      size: this.pageSize,
      corridorOrigin: value.corridorOrigin,
      corridorDestination: value.corridorDestination,
      equipmentType: value.equipmentType,
      weightKg: value.weightKg,
      pallets: value.pallets,
      lang: this.languageService.currentLanguage()
    };
  }

  private toOptions(values?: string[] | null): MarketplaceFilterOption[] {
    return (values ?? []).map(value => ({ label: value, value }));
  }

  private preferredOptions(
    options?: MarketplaceFilterOption[] | null,
    values?: string[] | null
  ): Array<{ label: string; value: string }> {
    return options?.length ? options : this.toOptions(values);
  }

  private parseDate(value?: string | null): Date | null {
    if (!value) {
      return null;
    }
    const date = new Date(this.normalizeDateValue(value));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private normalizeDateValue(value: string): string {
    return /(?:Z|[+-]\d{2}:\d{2})$/i.test(value) ? value : `${value}Z`;
  }

  private trustSignalText(trust: PublicTrustSummary): string {
    const signals = [
      this.labelize(trust.verificationStatus),
      this.labelize(trust.paymentConfidence),
      this.labelize(trust.assuranceStatus)
    ].filter(Boolean);
    return signals.join(' | ');
  }

  private extractFilterTerm(event: unknown): string {
    const filter = (event as { filter?: unknown })?.filter;
    return typeof filter === 'string' ? filter.trim() : '';
  }

  private setLazyOptions(
    type: 'origin' | 'destination' | 'equipment',
    values: string[],
    options: MarketplaceFilterOption[] = this.toOptions(values)
  ): void {
    this.filterOptions = {
      ...this.filterOptions,
      corridorOrigins: type === 'origin' ? values : this.filterOptions.corridorOrigins,
      corridorDestinations: type === 'destination' ? values : this.filterOptions.corridorDestinations,
      equipmentTypes: type === 'equipment' ? values : this.filterOptions.equipmentTypes,
      corridorOriginOptions: type === 'origin' ? options : this.filterOptions.corridorOriginOptions,
      corridorDestinationOptions: type === 'destination' ? options : this.filterOptions.corridorDestinationOptions,
      equipmentTypeOptions: type === 'equipment' ? options : this.filterOptions.equipmentTypeOptions
    };
  }

  private locale(): string {
    return this.languageService.currentLanguage() === 'ar' ? 'ar-SA' : 'en';
  }

  private loadFilterOptions(): void {
    this.service.getAvailabilityFilterOptions({ lang: this.languageService.currentLanguage() }).subscribe({
      next: options => (this.filterOptions = this.mergeSelectedFilterOptions(options)),
      error: () => {
        this.filterOptions = this.mergeSelectedFilterOptions({
          corridorOrigins: [],
          corridorDestinations: [],
          equipmentTypes: [],
          currencies: [],
          corridorOriginOptions: [],
          corridorDestinationOptions: [],
          equipmentTypeOptions: []
        });
      }
    });
  }

  private persistFilterState(): void {
    if (this.suppressFilterPersistence || !this.hasBrowserStorage()) {
      return;
    }

    const value = this.form.getRawValue();
    const hasValue = Object.values(value).some(item => `${item ?? ''}`.trim().length > 0);
    try {
      if (hasValue || this.restoredPageNumber > 0 || (this.pendingScrollRestore ?? this.currentScrollY()) > 0) {
        localStorage.setItem(PublicAvailabilitiesListComponent.FILTER_STORAGE_KEY, JSON.stringify({
          filters: value,
          pageNumber: this.restoredPageNumber,
          scrollY: this.pendingScrollRestore ?? this.currentScrollY()
        }));
      } else {
        localStorage.removeItem(PublicAvailabilitiesListComponent.FILTER_STORAGE_KEY);
      }
    } catch {
      // Ignore storage failures; filters should still work without persistence.
    }
  }

  private restoreFilterState(): void {
    if (!this.hasBrowserStorage()) {
      return;
    }

    try {
      const raw = localStorage.getItem(PublicAvailabilitiesListComponent.FILTER_STORAGE_KEY);
      if (!raw) {
        return;
      }
      const saved = JSON.parse(raw) as {
        filters?: Record<string, string | number | null>;
        pageNumber?: number;
        scrollY?: number;
      } & Record<string, string | number | null | undefined>;
      const filters = saved.filters ?? saved;
      this.form.patchValue({
        corridorOrigin: this.restoreString(filters['corridorOrigin']),
        corridorDestination: this.restoreString(filters['corridorDestination']),
        equipmentType: this.restoreString(filters['equipmentType']),
        weightKg: this.restoreString(filters['weightKg']),
        pallets: this.restoreString(filters['pallets'])
      });
      this.restoredPageNumber = Number.isFinite(saved.pageNumber) ? Number(saved.pageNumber) : 0;
      this.pendingScrollRestore = Number.isFinite(saved.scrollY) ? Number(saved.scrollY) : null;
      this.filterOptions = this.mergeSelectedFilterOptions(this.filterOptions);
    } catch {
      this.clearStoredFilterState();
    }
  }

  private mergeSelectedFilterOptions(options: PublicAvailabilityFilterOptions): PublicAvailabilityFilterOptions {
    const value = this.form.getRawValue();
    return {
      ...options,
      corridorOrigins: this.includeSelectedValue(options.corridorOrigins, value.corridorOrigin),
      corridorDestinations: this.includeSelectedValue(options.corridorDestinations, value.corridorDestination),
      equipmentTypes: this.includeSelectedValue(options.equipmentTypes, value.equipmentType),
      corridorOriginOptions: this.includeSelectedOption(options.corridorOriginOptions, value.corridorOrigin),
      corridorDestinationOptions: this.includeSelectedOption(options.corridorDestinationOptions, value.corridorDestination),
      equipmentTypeOptions: this.includeSelectedOption(options.equipmentTypeOptions, value.equipmentType)
    };
  }

  private includeSelectedValue(values: string[] = [], selected?: string | null): string[] {
    const value = (selected || '').trim();
    if (!value || values.includes(value)) {
      return values;
    }
    return [value, ...values];
  }

  private includeSelectedOption(
    options: MarketplaceFilterOption[] = [],
    selected?: string | null
  ): MarketplaceFilterOption[] {
    const value = (selected || '').trim();
    if (!value || options.some(option => option.value === value)) {
      return options;
    }
    return [{ label: value, value }, ...options];
  }

  private restoreString(value: unknown): string {
    return typeof value === 'string' || typeof value === 'number' ? `${value}` : '';
  }

  private clearStoredFilterState(): void {
    if (!this.hasBrowserStorage()) {
      return;
    }
    try {
      localStorage.removeItem(PublicAvailabilitiesListComponent.FILTER_STORAGE_KEY);
    } catch {
      // Ignore storage failures.
    }
  }

  private hasBrowserStorage(): boolean {
    return typeof localStorage !== 'undefined';
  }

  private restoreScrollPositionOnce(): void {
    const scrollY = this.pendingScrollRestore;
    if (scrollY === null || typeof window === 'undefined') {
      return;
    }

    setTimeout(() => {
      window.scrollTo({ top: scrollY, behavior: 'auto' });
      this.pendingScrollRestore = null;
    });
  }

  private currentScrollY(): number {
    return typeof window === 'undefined' ? 0 : window.scrollY || 0;
  }
}
